// Monatslogik des DatePickers (DESIGN §3.6.7) mit date-fns; Woche beginnt Montag (en-GB).
// Werte als ISO-Datum „yyyy-MM-dd“ (Datum ohne Zeitzone). Reine Funktionen, getestet.
import {
  addDays,
  addMonths,
  addYears,
  endOfQuarter,
  endOfWeek,
  format,
  isValid,
  parse,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

export const WEEK_STARTS_ON = 1 as const;

/** 6 × 7 Tage ab dem Wochenbeginn vor dem Monatsersten. */
export function monthGrid(month: Date): Date[] {
  const start = startOfWeek(startOfMonth(month), { weekStartsOn: WEEK_STARTS_ON });
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

export const toIso = (date: Date) => format(date, 'yyyy-MM-dd');

/** ISO-Datum → lokales Datum (Mitternacht), ungültig → null. */
export function fromIso(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = parse(iso, 'yyyy-MM-dd', new Date(2000, 0, 1));
  return isValid(date) ? date : null;
}

/** Anzeige „25 Oct 2026“ (DESIGN §4.8). */
export const formatDisplay = (date: Date) => format(date, 'd MMM yyyy');

/** Name einer Zelle: „Sunday, 25 October 2026“. */
export const formatCellLabel = (date: Date) => format(date, 'EEEE, d MMMM yyyy');

const INPUT_FORMATS = [
  'd MMM yyyy',
  'd MMMM yyyy',
  'yyyy-MM-dd',
  'dd/MM/yyyy',
  'd/M/yyyy',
  'd.M.yyyy',
  'MMM d, yyyy',
];

/** Freie Eingabe → Datum; akzeptiert „25 Oct 2026“, „2026-10-25“, „25/10/2026“. */
export function parseDateInput(text: string): Date | null {
  const trimmed = text.trim().replace(/\s+/g, ' ');
  if (!trimmed) return null;
  for (const pattern of INPUT_FORMATS) {
    const date = parse(trimmed, pattern, new Date(2000, 0, 1));
    if (isValid(date) && date.getFullYear() >= 1900 && date.getFullYear() <= 2200) return date;
  }
  return null;
}

/** Tastatur im Raster: Pfeile Tag/Woche, PageUp/PageDown Monat (Shift: Jahr), Home/End Woche. */
export function moveByKey(date: Date, key: string, shift = false): Date | null {
  switch (key) {
    case 'ArrowLeft':
      return addDays(date, -1);
    case 'ArrowRight':
      return addDays(date, 1);
    case 'ArrowUp':
      return addDays(date, -7);
    case 'ArrowDown':
      return addDays(date, 7);
    case 'PageUp':
      return shift ? addYears(date, -1) : addMonths(date, -1);
    case 'PageDown':
      return shift ? addYears(date, 1) : addMonths(date, 1);
    case 'Home':
      return startOfWeek(date, { weekStartsOn: WEEK_STARTS_ON });
    case 'End':
      return endOfWeek(date, { weekStartsOn: WEEK_STARTS_ON });
    default:
      return null;
  }
}

/** Schnellwahl relativ zu „heute“ (APP_TODAY). */
export const quickPicks = (today: Date) => [
  { label: 'Today', date: today },
  { label: '+1 week', date: addDays(today, 7) },
  { label: 'End of quarter', date: endOfQuarter(today) },
];

/** Liegt `iso` zwischen `from` und `to` (beide inklusive)? ISO-Strings vergleichen lexikografisch. */
export function inRange(iso: string, from?: string | null, to?: string | null): boolean {
  if (!from || !to) return false;
  return iso >= from && iso <= to;
}
